import { useSnackbar } from "notistack";
import { FC, useEffect, useState } from "react";

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, MenuItem, Select, Stack, TextField } from "@mui/material";

import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { api } from "../../../../../utils";
import { IError } from "../../../../../models/ReportPageModels";
import { BillingCompanyModel } from "../../IccGeneral/CreateReportUser/CreateReportUser";

export interface XeroContactModel {
    id: string;
    name: string;
}

interface AdminGetBillingCompaniesResponse {
    data: BillingCompaniesData;
    error: IError;
}

interface BillingCompaniesData {
    companies: BillingCompanyModel[];
}

interface AdminGetXeroContactsResponse {
    data: XeroContactsData;
    error: IError;
}

interface XeroContactsData {
    contacts: XeroContactModel[];
}

interface CreateBillingUserRequest {
    username: string;
    company: number;
    currency: string;
    deduplication: number;
    xeroContact: string;
    invoiceReference: string;
}

interface CreateBillingUserResponse {
    data: CreateBillingUserData;
    error: IError;
}
interface CreateBillingUserData {
    status: string;
}

const defaultInvoiceReference: string = "<month> <year> monthly charges";
const allCurrencies: string[] = ["GBP £", "DKK", "EURO €"];

interface CreateBillingUserProps {
    workflowNextStep?: () => void;
    createdBranchName?: string | null;
}

const CreateBillingUser: FC<CreateBillingUserProps> = ({ workflowNextStep = null, createdBranchName = null }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [username, setUsername] = useState<string>("");
    const [selectedCompany, setSelectedCompany] = useState<number>(0);
    const [selectedCurrency, setSelectedCurrency] = useState<string>("GBP £");
    const [deduplicationDays, setDeduplicationDays] = useState<number>(0);
    const [selectedXeroContact, setSelectedXeroContact] = useState<string>("");
    const [invoiceReference, setInvoiceReference] = useState<string>(defaultInvoiceReference);

    const [companies, setCompanies] = useState<BillingCompanyModel[]>([]);
    const [xeroContacts, setXeroContacts] = useState<XeroContactModel[]>([]);

    const [reminderOpen, setReminderOpen] = useState<boolean>(false);

    useEffect(() => {
        GetCompanies();
        GetXeroContacts();
    }, []);

    const GetCompanies = async () => {
        try {
            const response: AdminGetBillingCompaniesResponse = await api.get("api/Admin/GetBillingCompanies", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setCompanies(response.data.companies);

                // branch created in workflow previous step
                if (createdBranchName) {
                    const company = response.data.companies.find(company => company.branchName === createdBranchName);
                    if (company) {
                        setSelectedCompany(company.id);
                    }
                }
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const GetXeroContacts = async () => {
        try {
            const response: AdminGetXeroContactsResponse = await api.get("api/Xero/GetContacts", {
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                setXeroContacts(response.data.contacts)
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const CreateClient = async () => {
        if (username === "" || selectedCompany === 0 || selectedXeroContact === "") {
            enqueueSnackbar("Please recheck fields", { variant: "error" });
            return;
        }

        var body: CreateBillingUserRequest = {
            username: username,
            company: selectedCompany,
            currency: selectedCurrency,
            deduplication: deduplicationDays,
            xeroContact: selectedXeroContact,
            invoiceReference: invoiceReference
        };

        try {
            const response: CreateBillingUserResponse = await api.post("api/Admin/CreateBillingUser", {
                json: body,
                headers: {
                    "Authorization": localStorage.getItem("token")!
                }
            }).json();

            if (response.error == null) {
                enqueueSnackbar("Created!", { variant: "success" });

                // for workflow function to step passed
                if (workflowNextStep)
                    workflowNextStep();

                // clear fields
                setUsername("");
                setSelectedCompany(0);
                setSelectedCurrency("GBP £");
                setDeduplicationDays(0);
                setSelectedXeroContact("");
                setInvoiceReference(defaultInvoiceReference);
                // reminder
                setReminderOpen(true);
            }
            else {
                enqueueSnackbar(response.error.description, { variant: "error" });
            }
        }
        catch (err) {
            enqueueSnackbar((err as Error).message, { variant: "error" });
        }
    }

    const handleClose = () => {
        setReminderOpen(false);
    };

    return (
        <Stack>
            {/* Username */}
            <Stack
                direction={"row"}
                alignItems={"center"} >
                <p style={{ width: '110px' }}>Username: </p>
                <TextField
                    value={username}
                    onChange={(newValue) => { setUsername(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* Company */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Company: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCompany}
                        onChange={(newValue: any) => { setSelectedCompany(newValue.target.value); }}
                        size="small">
                        {companies.map((company, index) =>
                            <MenuItem
                                key={index}
                                value={company.id}>
                                {company.branchName}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Currency */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Currenct: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedCurrency}
                        onChange={(newValue: any) => { setSelectedCurrency(newValue.target.value); }}
                        size="small">
                        {allCurrencies.map((currency, index) =>
                            <MenuItem
                                key={index}
                                value={currency}>
                                {currency}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
            </Stack>

            {/* Deduplication */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Deduplication days: </p>
                <TextField
                    value={deduplicationDays}
                    onChange={(newValue: any) => { setDeduplicationDays(newValue.target.value) }}
                    sx={{ minWidth: "245px" }}
                    size="small" />
            </Stack>

            {/* XeroContact */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>XeroContact: </p>
                <FormControl sx={{ minWidth: "245px" }}>
                    <Select
                        value={selectedXeroContact}
                        onChange={(newValue: any) => { setSelectedXeroContact(newValue.target.value); }}
                        size="small">
                        {xeroContacts.map((contanct, index) =>
                            <MenuItem
                                key={index}
                                value={contanct.id}>
                                {contanct.name}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>
                <IconButton
                    color="primary"
                    size="large"
                    onClick={() => { GetXeroContacts() }}>
                    <RefreshIcon sx={{ fontSize: 26 }} />
                </IconButton>
            </Stack>

            {/* Invoice Reference */}
            <Stack
                direction={"row"}
                alignItems={"center"}>
                <p style={{ width: '110px' }}>Invoice Reference: </p>
                <TextField
                    value={invoiceReference}
                    onChange={(newValue: any) => { setInvoiceReference(newValue.target.value) }}
                    sx={{ minWidth: "290px" }}
                    size="small" />
            </Stack>

            {/* Create and info buttons*/}
            <Stack
                direction={"row"}>
                <Button
                    variant="outlined"
                    onClick={() => CreateClient()}
                    sx={{ width: '82px' }}>
                    Create
                </Button>

                <IconButton
                    color="primary"
                    size="large"
                    sx={{ width: "30px", height: "30px", left: "20px", top: "5px" }}
                    onClick={() => { setReminderOpen(true); }}>
                    <InfoOutlinedIcon />
                </IconButton>
            </Stack>



            {/* Reminder */}
            <Dialog
                open={reminderOpen}
                onClose={handleClose}>
                <DialogTitle>
                    {"Reminder"}
                </DialogTitle>
                <DialogContent>
                    Once saved, make sure the tracking category is setup in Xero (Recurring Income projects) and then in the ALIT Dashboard click on the Xero,
                    Tracking option to link it to the client record.  Once that is done, don't forget to apply the tracking to the clients billing record
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Ok</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    )
}

export default CreateBillingUser;